/**
 * The origin, as the client sees it.
 *
 * NO AUTHENTICATION CODE, AND THAT IS CORRECT RATHER THAN MISSING. Cloudflare
 * Access sits on the hostname; a browser that has logged in holds its cookie,
 * and Cloudflare injects `Cf-Access-Jwt-Assertion` into every request on the way
 * through. The Worker verifies that header and the origin verifies it AGAIN,
 * independently, because it must never trust the edge about who the caller is.
 * A `fetch` from this origin therefore arrives authenticated with nothing added
 * here — and there is nothing this code COULD add, since it cannot read the
 * cookie and must not try.
 *
 * Every shape below was read off `apps/mailgw/src/api/app.ts` rather than
 * inferred, including the awkward ones: `flags` really is one space-delimited
 * string with a leading and trailing space, `has_attachment` really is 0 | 1
 * rather than a boolean, and `date` really is unix SECONDS.
 *
 * IT REACHES FOR THE SCREEN EXACTLY ONCE, for the one condition no call site can
 * do anything about: an Access session that has ended. See `isSignedOut`.
 */
import { showSignedOut } from './SignedOut.tsx'

/** Every non-2xx body on this surface, without exception. */
export interface ApiError {
  readonly error: string
  readonly code: string
  /**
   * Whether the CLIENT can put itself right and retry.
   *
   * Exactly one code answers yes — `cursor_stale`, meaning "this walk cannot
   * continue, start it again from page 1". It is a field rather than a code
   * table so a client never needs to enumerate the other twenty-one.
   */
  readonly recoverable: boolean
  readonly [key: string]: unknown
}

export class HearthError extends Error {
  readonly status: number
  readonly code: string
  readonly recoverable: boolean
  readonly body: ApiError | null

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error ?? fallback)
    this.name = 'HearthError'
    this.status = status
    this.code = body?.code ?? 'unknown'
    this.recoverable = body?.recoverable ?? false
    this.body = body
  }
}

/**
 * The code this module puts on the one failure the server never gets to
 * describe.
 *
 * An expired Access session is answered by CLOUDFLARE, before the Worker and
 * long before the origin, with a 302 to a login page on the team domain. There
 * is no Hearth envelope in it and there never will be, so the code is minted
 * here — and it is a code rather than a magic status because `HearthError`
 * already carries one and every consumer already reads it.
 */
export const SIGNED_OUT = 'signed_out'

const SIGNED_OUT_BODY: ApiError = {
  error: 'Your sign-in has expired.',
  code: SIGNED_OUT,
  // NOT recoverable. `recoverable` means "the client can put itself right and
  // retry", and this client cannot: only a person going back through the
  // identity provider can.
  recoverable: false,
}

/**
 * Is this error "you are signed out", as opposed to "that request failed"?
 *
 * ONE PREDICATE, EXPORTED ONCE, because five surfaces would otherwise each
 * invent their own idea of it — and four of them would forget the second shape.
 *
 * THE TWO SHAPES IT ANSWERS TRUE FOR:
 *
 *   - The Access login page, which `request` recognises as a redirect rather
 *     than as an answer and tags with `SIGNED_OUT`. Its status is whatever the
 *     runtime chose to report — 0 in a browser, the 302 itself elsewhere — which
 *     is exactly why this reads the code and not the status.
 *   - A 401 whose code says the identity itself was not established — mailgw's
 *     `unauthenticated`, or no Hearth envelope at all, which is the shape the
 *     Worker's `{"error":"Not authenticated"}` and Cloudflare's own refusals
 *     take.
 *
 * AND THE ONE IT DELIBERATELY ANSWERS FALSE FOR. The warden refuses a
 * destructive verb with **401 `reauth_required`**, and mailgw forwards that
 * status and body verbatim. That caller is signed in — they are being asked for
 * a second factor, which is a different sentence and a different remedy — so
 * "any 401 means signed out" would put a "your session expired, sign in again"
 * bar in front of somebody whose session is fine, and send them round a loop
 * that cannot end. The code is what tells them apart, which is why this reads it
 * rather than the status alone.
 */
export function isSignedOut(error: unknown): boolean {
  if (!(error instanceof HearthError)) return false
  if (error.code === SIGNED_OUT) return true
  return error.status === 401 && (error.code === 'unauthenticated' || error.code === 'unknown')
}

/**
 * An error as one clause of a sentence, never "[object Object]".
 *
 * ONE COPY, HERE, because every surface that catches from this module needs the
 * same three lines and the interesting half is which errors have no message at
 * all: a `TypeError: Failed to fetch` has one, and a rejected value that is not
 * an `Error` — which is what a runtime hands over when a promise is rejected
 * with a string — has none. Written per panel, that check is what somebody
 * forgets, and the reader gets "That could not be saved: undefined".
 */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message !== '' ? error.message : fallback
}

/**
 * What a 403 refused, and whether the tailnet would have helped.
 *
 * ONE READER OF THE SHAPE, beside `isSignedOut` and for the same reason: the
 * origin's `require()` answers every capability refusal as
 * `{code: 'forbidden', capability, tailnetOnly}` (`apps/mailgw/src/api/app.ts`),
 * and a surface that only looked at the status cannot tell "your role does not
 * have this" from "your role has it and this path cannot exercise it" — which
 * are different sentences with different remedies, one of them being "open
 * Hearth over Tailscale". Every consumer that hides a control on a refusal needs
 * the capability NAME too, or it hides the wrong control.
 *
 * NULL FOR EVERYTHING ELSE, INCLUDING SOME 403s. The listener's own refusal
 * (`AuthError` with status 403) carries no `capability`, and so does a warden
 * refusal forwarded verbatim; those are answered with `capability: null` rather
 * than by inventing one, because a caller that switched a control off on a
 * guessed name would switch off a control the origin never mentioned.
 */
export interface Forbidden {
  /** The capability the origin named, or null when it named none. */
  readonly capability: string | null
  /** True when the ROLE holds it and only this path cannot exercise it. */
  readonly tailnetOnly: boolean
  /** The origin's own sentence. Never rewritten by a caller. */
  readonly message: string
}

export function forbiddenBy(error: unknown): Forbidden | null {
  if (!(error instanceof HearthError)) return null
  if (error.status !== 403 || error.code !== 'forbidden') return null
  const body = error.body as { capability?: unknown; tailnetOnly?: unknown } | null
  return {
    capability: typeof body?.capability === 'string' ? body.capability : null,
    tailnetOnly: body?.tailnetOnly === true,
    message: error.message,
  }
}

/**
 * Is this response the Access login page, rather than an answer from Hearth?
 *
 * TWO SPELLINGS OF ONE FACT, because two runtimes report it differently and the
 * client is tested in one and shipped in the other. A BROWSER, under
 * `redirect: 'manual'`, filters a redirect down to an opaque response: status 0,
 * type `opaqueredirect`, nothing readable in it at all. Node's `fetch` — and
 * every test double — hands back the redirect itself, so the status is what
 * shows. Checking only the first would make every test of this a test of
 * something else.
 *
 * 304 IS DELIBERATELY EXCLUDED. It is in the 3xx range and it is not a redirect:
 * it is a cache validator, and mailgw answers one to a conditional GET. Nothing
 * in this client sends `If-None-Match` today, and a signed-out bar in front of a
 * reader whose settings were merely unchanged would be an odd way to find out
 * that somebody added one.
 */
function isAccessLogin(response: Response): boolean {
  if (response.type === 'opaqueredirect' || response.status === 0) return true
  return response.status >= 300 && response.status < 400 && response.status !== 304
}

/**
 * Turn a failed response into the error to throw, and put the bar on screen if
 * the session is what failed.
 *
 * THE ONE PLACE, which is the whole point of it existing. `request` is where
 * every JSON call lands and `restagePart` is the single raw `fetch` beside it;
 * both come through here, so neither can drift into a second opinion about what
 * an Access redirect looks like — and no call site anywhere else has to have one.
 */
async function failure(response: Response, fallback: string): Promise<HearthError> {
  if (isAccessLogin(response)) {
    const signedOut = new HearthError(response.status, SIGNED_OUT_BODY, SIGNED_OUT_BODY.error)
    showSignedOut()
    return signedOut
  }

  // THE ENVELOPE IS GUARANTEED, THE PARSE IS NOT. A 502 from Cloudflare is HTML,
  // and `response.json()` on it throws something that names neither the request
  // nor the status. The fallback keeps the status, which is the only thing that
  // is always true.
  const body = (await response.json().catch(() => null)) as ApiError | null
  const error = new HearthError(response.status, body, fallback)
  if (isSignedOut(error)) showSignedOut()
  return error
}

export interface MessageRow {
  /** The X-GUID. What every other route takes. */
  readonly id: string
  readonly message_id: string | null
  readonly thread_id: string | null
  /** Unix SECONDS, not milliseconds. */
  readonly date: number
  readonly size: number
  readonly subject: string | null
  /** The raw header value — `Anna Whitfield <anna@example.com>`, unparsed. */
  readonly from_addr_name: string | null
  /** The raw header value, unparsed, exactly like `from_addr_name` above it. */
  readonly to_addrs: string | null
  /**
   * The raw Cc: header. THE FOURTEENTH COLUMN, AND IT EXISTS FOR REPLY-ALL.
   *
   * No list column renders it. It is on every row because a reply-all has to be
   * addressed correctly at the moment the composer opens — the reader can type
   * and press Send inside the second a fetch would take, and a recipient that
   * arrives late is a message sent to the wrong people rather than a cosmetic
   * gap. `store.ts`'s `ROW_COLUMNS` carries what it costs per page, measured.
   *
   * Without it `replyAllRecipients` had to build Cc out of `to_addrs`, which
   * dropped everybody who was on Cc and demoted everybody who was on To.
   */
  readonly cc_addrs: string | null
  readonly mailbox: string
  readonly uid: number
  /** One space-delimited string WITH a leading and trailing space, or ''. */
  readonly flags: string
  /** 0 | 1, not a boolean. */
  readonly has_attachment: number
  readonly preview: string | null
  /**
   * When this message wakes, in Unix SECONDS, or null when it is not asleep.
   *
   * THE MAILBOX IS NOT THE PREDICATE. Snoozing MOVES the message into whichever
   * mailbox carries `\\Archive`, and that is also where archiving puts things —
   * so a client that inferred "asleep" from `mailbox` would offer "Bring back
   * now" on every message the reader filed deliberately, and pressing it would
   * move it back to the Inbox. `is:snoozed` compiles against this same column at
   * the origin, so this is one fact with one reader rather than two.
   *
   * OPTIONAL, because an origin older than the projection is a working origin:
   * absent reads as "not asleep", which is what every message was before the
   * column was sent.
   */
  readonly snoozed_until?: number | null
  /**
   * Which of this account's addresses the mail was sent to, before any alias
   * rewriting — `X-Original-To` — or null.
   *
   * THE ONE HEADER THAT NAMES A DISPOSABLE ADDRESS. To: and Cc: carry it when
   * the sender wrote to it directly, which is the ordinary case and what a
   * signup confirmation looks like; this is what says so when the mail arrived
   * by Bcc or through a list. `selectIdentity` asks for it FIRST for that
   * reason, and a reply chosen without it goes out from the household's real
   * address.
   *
   * NULL ON EVERYTHING INGESTED BEFORE THE ORIGIN LEARNED TO FETCH IT, so the
   * fall-through to To: and Cc: is not a fallback for broken mail — it is what
   * the whole archive answers.
   */
  readonly original_to?: string | null
  /**
   * How many messages are in this row's thread. ONLY on a `group=thread` page,
   * and absent rather than 1 elsewhere — the message walk does not know the
   * number, and rendering "1" where it means "not counted" states a fact nobody
   * established.
   */
  readonly thread_count?: number
}

export interface SearchCount {
  readonly value: number
  /** False means the true count is GREATER than `value` — a bound, not a total. */
  readonly exact: boolean
}

/** The four answers the Screener offers: three ways of being wanted, and one not. */
export type SenderDisposition = 'people' | 'news' | 'receipts' | 'trash'

/** A sender with no decision yet — one card. */
export interface PendingSender {
  readonly addr: string
  readonly name: string
  readonly messages: number
  /** Unix SECONDS. The first message they sent, which is what the card previews. */
  readonly first_date: number
  readonly last_date: number
  readonly first_guid: string | null
}

export interface KnownSender {
  readonly addr: string
  readonly disposition: SenderDisposition
  /** `seed` was derived from the archive; `decided` is somebody's answer. */
  readonly source: 'seed' | 'decided'
  readonly decided_at: number
}

export type ConsoleTransport = 'imap' | 'managesieve' | 'smtp'

export interface ConsoleStatus {
  readonly available: boolean
  /** Whether this principal may arm at all, told before they try. */
  readonly canArm: boolean
  /** Milliseconds left on a live arming, or 0. */
  readonly armedFor: number
  readonly verbs: Readonly<Record<ConsoleTransport, { read: string[]; armed: string[] }>>
}

export interface ConsoleExchange {
  readonly tag: string
  readonly class: 'read' | 'armed'
  readonly verb: string
  readonly sent: string
  readonly received: string
  readonly ms: number
  readonly bytes: number
  readonly truncated: boolean
}

export interface WardenPlan {
  readonly applied: false
  readonly token: string
  readonly verb: string
  readonly summary: string
  /** One unified diff per file the change touches. THE DIFF IS THE PRODUCT. */
  readonly diffs: readonly { file: string; diff: string }[]
  readonly destructive: boolean
  readonly because?: string
  /** A generated password, handed over once and never stored anywhere readable. */
  readonly secret?: string
  /** Whether this caller will be able to apply it, told before they try. */
  readonly needsReauth: boolean
  readonly reqid: string
}

export interface WardenAudit {
  readonly commit: string
  readonly message: string
  /** Unix SECONDS. */
  readonly at: number
}

/**
 * One thing the origin could not do exactly, as a sentence a person can act on.
 *
 * `code` IS FOR THE CLIENT AND `message` IS FOR THE READER. The client must
 * never rewrite the sentence: the origin knows which term was floored and by
 * how much, and a second copy of that wording here would be one edit away from
 * describing a different limit than the one that fired.
 */
export interface SearchNote {
  readonly code: string
  /** The term the note is about, so a UI can point at it. May be empty. */
  readonly term: string
  readonly message: string
}

export interface SearchPage {
  readonly query: string
  readonly messages: readonly MessageRow[]
  /**
   * The ONLY end-of-walk signal. A short page is ordinary — the scan budget cuts
   * one whenever a query is expensive — so a client that stops on
   * `messages.length < limit` loses mail.
   */
  readonly nextCursor: string | null
  readonly order: 'asc' | 'desc'
  /** The CLAMPED limit. Ask for 9999 and this says 200. */
  readonly limit: number
  /** `null` means NOT COUNTED: pages 2..n under the default `count=1`. */
  readonly count: SearchCount | null
  readonly seq: number
  /**
   * What the origin could not do exactly, in words meant for a reader.
   *
   * TYPED, BECAUSE `unknown[]` MEANT NOBODY COULD RENDER IT. The origin emits
   * these — a body term floored to one page, a term whose matches hit the
   * server's 10,000 cap — and the client dropped every one, which turns "this
   * list is incomplete and here is why" into a short list that looks complete.
   * That is the same failure class the notes were added to fix.
   */
  readonly notes?: readonly SearchNote[]
  /**
   * The grouping the server actually applied. Echoed like `order` and `limit`,
   * and worth checking for the same reason: a conversation page and a message
   * page are the same shape on the wire, so a `group=thread` that never arrived
   * comes back as rows that look entirely correct.
   */
  readonly group: 'message' | 'thread'
}

export interface MailboxState {
  readonly name: string
  readonly uidvalidity: number
  readonly uidnext: number | null
  readonly highestModseq: number | null
  /** The IMAP SPECIAL-USE flag — `\\Archive`, `\\Sent`. NO folder is special-cased by NAME. */
  readonly specialUse: string | null
  readonly subscribed: boolean
  readonly backfillCursor: number | null
}

export interface Whoami {
  readonly email: string
  readonly account: string
  readonly role: 'owner' | 'adult' | 'child'
  readonly via: 'access' | 'tailscale' | 'dev'
  readonly capabilities: readonly string[]
  /** Held by the role but not exercisable on this path. Show them disabled, not hidden. */
  readonly tailnetOnly: readonly string[]
}

/**
 * One address, and every mailbox it delivers into.
 *
 * `destinations` IS A LIST BECAUSE THE FILE IS. It is the right-hand column of
 * `postfix-virtual.cf`, which is whitespace-separated and routinely fans one
 * address out to two people — `family@` reaching both parents is the ordinary
 * case, not an exotic one. Typed as a single string it would print
 * "dh@h-group.net anna@h-group.net" as one mailbox nobody has.
 */
export interface AddressEntry {
  readonly source: string
  readonly destinations: readonly string[]
  /**
   * Whether the server would accept a request to take this one away.
   *
   * IT IS THE SERVER'S OWN FIRST GATE, ANSWERED ONCE. `alias.delete` and
   * `masked.revoke` both run `checkAddress` before they look at anything else,
   * and a hand-edited `postfix-virtual.cf` can hold lines it refuses —
   * `@h-group.net` as a catch-all, a local part in capitals, a domain with a
   * trailing dot. Without this the panel offered a Delete button beside each of
   * them that answered 400 every time it was pressed. Deciding it here instead
   * would be a second copy of a rule this side does not hold.
   *
   * OPTIONAL, because an origin older than this field is a working origin: the
   * panel treats its absence as "yes" and gets the behaviour it had before,
   * rather than hiding every control against a server that never said no.
   */
  readonly removable?: boolean
  /**
   * What has actually arrived here, on the disposable addresses only.
   *
   * DERIVED AT THE ORIGIN FROM THE INDEX, not counted into a table: every one of
   * these messages is already indexed, so the answer is as old as the archive
   * rather than starting at zero on the day a counter was added. A named alias
   * carries none of it — the fields are simply absent — because the question
   * "who is still using this" is the one a DISPOSABLE address is revoked on.
   *
   * `messages: 0` IS AN ANSWER AND ABSENT IS NOT. An address minted this morning
   * has a real, knowable zero, and that is exactly the state somebody checks
   * when they wonder whether a signup ever went through.
   */
  readonly messages?: number
  /** Unix SECONDS of the newest message, or null when none has arrived. */
  readonly lastAt?: number | null
  /** The busiest few senders, heaviest first. Bounded by the origin. */
  readonly senderDomains?: readonly { domain: string; messages: number }[]
  /** How many distinct sender domains there are, including those not listed. */
  readonly senderDomainsTotal?: number
  /**
   * The site this address was minted for, as the person typed it.
   *
   * NOT THE HINT IN THE ADDRESS. `m.7fq2.petco.k3n9wq4x@…` carries `petco` —
   * the first label, stripped and cut to twelve characters, so a person can tell
   * one address from another in a list — and it cannot be turned back into a
   * site: `petco.co.uk` and `petco.com` make the same one. The origin kept the
   * real answer at mint time, and absent means it was never told.
   */
  readonly forDomain?: string
  /**
   * Domains that have written here and are strangers to `forDomain`.
   *
   * AN ADDRESS MADE FOR ONE SHOP THAT HEARS FROM AN AD NETWORK IS EVIDENCE IT
   * WAS SOLD OR BREACHED, and it is the thing this product can say that no
   * webmail client does. Empty or absent means either that nothing strange has
   * written, or that nobody said what the address was for — which is why the
   * sender domains are listed beside it either way and the reader is never left
   * with only a verdict.
   */
  readonly leakedFrom?: readonly string[]
}

/**
 * Everything `GET /addresses` says about the caller's own addresses.
 *
 * THE SPLIT INTO TWO LISTS IS THE SERVER'S AND MUST NOT BE REDONE HERE. A masked
 * address is one on the masked SUBDOMAIN — the subdomain exists precisely so a
 * leak never exposes the household domain — so "is this one disposable" is
 * "does its domain equal the masked one", and only the side holding the
 * environment knows what that domain is. A client that decided it for itself
 * would need a second copy of the domain, and the first installation whose
 * masked subdomain is not `m.<household domain>` would get "Revoke" offered
 * against the family alias.
 *
 * `maskedDomain: null` IS AN ANSWER, NOT A MISSING FIELD: this installation has
 * no disposable-address subdomain configured, minting cannot work, and the
 * honest surface is no form at all rather than one whose only possible outcome
 * is a refusal naming an environment variable.
 */
export interface AddressBook {
  readonly maskedDomain: string | null
  /** Named routes into the house, on the shared domain. */
  readonly aliases: readonly AddressEntry[]
  /** Disposable ones, on the masked subdomain. */
  readonly masked: readonly AddressEntry[]
}

/**
 * The rule model, re-exported from core.
 *
 * `@hearth/core/rules` is the one definition; importing the TYPES here rather
 * than restating them is what stops the editor and the compiler disagreeing
 * about what a rule is — which is the failure that would show up as a script
 * that compiles and does something else.
 */
import type { DriftState, RuleSet } from '@hearth/core/rules'
export type { Action, Condition, DriftState, GuiRule, MatchNode, Rule, RuleSet } from '@hearth/core/rules'

/** One row of `/suggest`. */
export interface Suggestion {
  readonly address: string
  readonly name: string
  readonly messages: number
}

export interface AttachmentPart {
  readonly part: string
  readonly type: string
  readonly filename: string | null
  readonly size: number
  readonly inline: boolean
  readonly contentId: string | null
}

export interface MessageBody {
  readonly id: string
  readonly mailbox: string
  readonly uid: number
  readonly source: { readonly part: string; readonly type: string; readonly charset: string | null } | null
  /** True when the text was rendered down from HTML rather than taken as-is. */
  readonly rendered: boolean
  readonly text: string
  /**
   * The trailing quoted region, split off losslessly: `text` is a prefix of the
   * original, this is a suffix, and only whitespace falls between.
   */
  readonly quoted: string | null
  /**
   * The message has a displayable `text/html` part. NOT "there is a rich half
   * you have not been shown" — for an HTML-only message this is true AND that
   * same part is what was rendered into `text`.
   */
  readonly hasHtml: boolean
  readonly truncated: boolean
  readonly structureTruncated: boolean
  readonly attachments: readonly AttachmentPart[]
}

export interface MessageHtml {
  readonly id: string
  readonly mailbox: string
  readonly uid: number
  readonly part: string | null
  /**
   * UNSANITISED. Exactly what the sender wrote.
   *
   * It is safe to hold because it is a JSON string — a browser parses it as
   * data. It is never assigned to `innerHTML`, never put in a `srcdoc`, and
   * never touched by anything but `sanitiseHtml`, which filters it with the
   * browser's own parser immediately before it goes into the sandboxed frame.
   */
  readonly html: string
  readonly truncated: boolean
}

export interface OpResult {
  readonly id: string
  readonly ok: boolean
  readonly seq?: number
  readonly error?: string
  /**
   * Where a move landed. Absent when the server sent no COPYUID — not undoable.
   *
   * A `snooze` carries it too, because a snooze IS a move into the archive: it
   * is what lets `useOps` record the snooze in the undo queue as an ordinary
   * move and get a correct inverse for free.
   */
  readonly moved?: { readonly mailbox: string; readonly uidvalidity: number; readonly uid: number }
}

export type Op =
  | { readonly id: string; readonly op: 'flag'; readonly mailbox: string; readonly uid: number; readonly add?: string[]; readonly remove?: string[] }
  | { readonly id: string; readonly op: 'label'; readonly guid: string; readonly add?: string[]; readonly remove?: string[] }
  | { readonly id: string; readonly op: 'move'; readonly mailbox: string; readonly uid: number; readonly to: string }
  /**
   * Park a message until `until` (unix SECONDS), or `null` to bring it back now.
   *
   * SECONDS, NOT MILLISECONDS. The origin refuses anything more than ten years
   * out precisely to catch a `Date.now()` that was not divided, so a caller that
   * gets this wrong is told rather than snoozing the message until the year
   * 58000. Keyed by GUID because the wake time belongs to the message, not to
   * one of the mailboxes it is sitting in.
   */
  | { readonly id: string; readonly op: 'snooze'; readonly guid: string; readonly until: number | null }

/**
 * Where the origin parks a snoozed message, and where it brings it back to.
 *
 * A COPY OF A SERVER FACT, and the only honest way to say so is here. The origin
 * picks the same mailbox by the same SPECIAL-USE flag (`SNOOZE_SPECIAL_USE` in
 * `apps/mailgw/src/api/app.ts`, which carries the argument for the choice), and
 * the client cannot import it across the workspace boundary. It needs it for one
 * thing only: recording the snooze in the undo queue as the move it actually is,
 * so `optimistic.ts` can invert it. If the two ever disagree the cost is a
 * refused Undo, not a lost message — the origin, not this line, decides where
 * the mail goes.
 *
 * BY SPECIAL-USE, NEVER BY NAME. The folder may be called Archive, Archiv, Arkiv
 * or All Mail; `shell-ia.md` states the rule and this household's server is a
 * stock docker-mailserver whose owner can rename anything.
 */
export const SNOOZE_SPECIAL_USE = '\\Archive'
/** The one mailbox name IMAP guarantees, so there is no flag to derive it from. */
export const SNOOZE_WAKE_MAILBOX = 'INBOX'

export const API = '/_hx/v1'

/**
 * Where a person goes to prove, right now, that they are still themselves.
 *
 * A COPY OF A FACT THE EDGE OWNS, and the honest place to say so is here.
 * `REAUTH_PATH` in `apps/worker/src/routes.ts` is the definition — it is what
 * the Worker classifies, what `run_worker_first` lists, and what an operator
 * types into the Access dashboard — and this workspace cannot import across to
 * it. `SNOOZE_SPECIAL_USE` above is the same situation for the same reason.
 *
 * IF THE TWO DRIFT, the cost is a sentence in the Server panel naming a URL that
 * answers the SPA instead of a token: visible, annoying and not dangerous.
 * Nothing is authorised by this string.
 *
 * IT IS A PATH AND NOT A LINK, deliberately. The Server panel is a tailnet
 * surface — `admin.server` is tailnet-only — while the Access application is on
 * the public hostname, so a relative link would open the tailnet's own 404 and
 * an absolute one would need a hostname nothing tells this client. Until
 * `/whoami` carries the app host, the panel prints the path and the
 * administrator opens it where they already sign in.
 */
export const REAUTH_PATH = '/admin/reauth'

/**
 * `type/subtype`, for a type that came off somebody else's message.
 *
 * NARROWER THAN RFC 2045's token, deliberately: it covers every media type any
 * mailer emits and nothing that fails it is worth passing on, since the caller
 * falls back to `application/octet-stream` rather than refusing. The characters
 * that matter are CR and LF — `fetch` rejects a header value containing one,
 * and the origin copies whatever it is given straight into the `Content-Type`
 * of a message it builds.
 */
const MIME_TYPE = /^[A-Za-z0-9!#$&^_.+-]{1,127}\/[A-Za-z0-9!#$&^_.+-]{1,127}$/

/**
 * `manual`, AND IT IS WHAT MAKES A DEAD SESSION LEGIBLE.
 *
 * On the default `follow`, an expired Access session is a 302 the browser CHASES
 * to `<team>.cloudflareaccess.com` — a cross-origin GET with no CORS headers, so
 * `fetch` rejects with a bare `TypeError: Failed to fetch`. That is
 * indistinguishable from a dropped Wi-Fi link, and telling a reader on a train
 * that their sign-in expired is exactly as unhelpful as telling a signed-out
 * reader that the network is down.
 *
 * `manual` does not chase it. The redirect comes back as an opaque response —
 * status 0, type `opaqueredirect` — and a genuine network failure still throws.
 * One line, and the two stop being the same event.
 *
 * NOTHING ON THIS SURFACE LEGITIMATELY REDIRECTS. mailgw answers 304 to a
 * conditional GET (which this client never sends) and no 3xx anywhere else; the
 * Worker proxies to the origin with `redirect: 'manual'` of its own. If a route
 * ever does start redirecting, this is the line that would have to learn about
 * it.
 */
const NO_FOLLOW: RequestInit = { redirect: 'manual' }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API}${path}`, {
    ...init,
    // AFTER the spread, so a caller cannot re-enable following by accident. No
    // caller passes one; the ordering is what keeps that true.
    ...NO_FOLLOW,
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}), ...init?.headers },
  })
  if (response.ok) return (await response.json()) as T
  throw await failure(response, `${init?.method ?? 'GET'} ${path} failed with ${response.status}`)
}

/**
 * One row of `GET /_hx/v1/outbox`.
 *
 * THE BUILT MESSAGE IS NOT IN IT. The origin strips it because it is up to a
 * megabyte per row, which is also why this panel cannot offer to show or reuse
 * the text of something that failed.
 */
export interface OutboxRow {
  readonly id: number
  /** Only these three are listed: `sent` and `cancelled` rows are not pending. */
  readonly state: 'queued' | 'sending' | 'failed'
  readonly from: string
  /** Every envelope recipient, Bcc included — which is why they are not in a header. */
  readonly to: readonly string[]
  readonly subject: string | null
  /** Unix SECONDS, like every other time on this surface. */
  readonly sendAfter: number
  readonly attempts: number
  readonly lastError: string | null
  readonly createdAt: number
}

export const api = {
  whoami: () => request<Whoami>('/whoami'),

  mailboxes: () => request<{ mailboxes: MailboxState[]; seq: number }>('/mailboxes'),

  /**
   * Every label this account has used, heaviest first.
   *
   * WHAT A PICKER OFFERS INSTEAD OF AN EMPTY BOX. A free-text field over a
   * keyword space produces `invoices`, `Invoices` and `invoice` on three
   * messages, which `label:invoices` cannot then find together — and no amount
   * of care at the moment of typing prevents it. The list is the fix.
   */
  labels: () => request<{ labels: { label: string; messages: number }[]; seq: number }>('/labels'),

  /**
   * Mute a conversation — the half of a mute an op cannot do.
   *
   * `$muted` AND THE MOVE GO THROUGH `/ops`, with the optimistic patch and the
   * undo every other triage key gets. What neither can touch is the reply that
   * arrives tomorrow, and that is this: the origin appends the thread's root
   * Message-ID to a generated Sieve rule and INSTALLS IT BEFORE ANSWERING. A
   * mute that marked what was on screen and left the rule for later would say a
   * conversation is silenced while every reply kept landing in the Inbox, which
   * is worse than having no mute.
   *
   * SO A REFUSAL HERE MEANS NOT MUTED, and the caller must not mark anything.
   * The origin writes nothing unless the rule is running.
   */
  mute: (threadId: string) =>
    request<{ muted: string; threads: number }>('/mute', { method: 'POST', body: JSON.stringify({ threadId }) }),

  unmute: (threadId: string) =>
    request<{ unmuted: boolean; threads: number }>('/mute/remove', {
      method: 'POST',
      body: JSON.stringify({ threadId }),
    }),

  /** Every muted conversation, newest first, with the subject the index holds. */
  mutedThreads: () =>
    request<{ threads: { threadId: string; mutedAt: number; subject: string | null }[]; seq: number }>('/mute'),

  /**
   * One page of a search.
   *
   * `cursor` CARRIES THE FROZEN PLAN — the query fingerprint, the driver, the
   * order, and the `now` the first page resolved relative dates against — so
   * changing `q` or `order` mid-walk is `cursor_mismatch` rather than a new
   * search. Start a new walk instead of amending one.
   */
  search: (params: {
    q?: string
    cursor?: string
    limit?: number
    order?: 'asc' | 'desc'
    count?: '1' | 'none' | 'exact'
    group?: 'message' | 'thread'
  }) => {
    const query = new URLSearchParams()
    if (params.q !== undefined) query.set('q', params.q)
    // A cursor already pins the query and the order; re-sending them is
    // harmless but re-sending a DIFFERENT one is the mismatch above.
    if (params.cursor) query.set('cursor', params.cursor)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.order) query.set('order', params.order)
    if (params.count) query.set('count', params.count)
    // THE GROUPING IS PART OF THE FROZEN PLAN too — it is hashed into the
    // cursor's fingerprint — so flipping it mid-walk is `cursor_mismatch` and
    // not a re-grouping of the page in hand.
    if (params.group) query.set('group', params.group)
    return request<SearchPage>(`/search?${query}`)
  },

  /**
   * The account's settings document, and the etag that versions it.
   *
   * ACCOUNT-SCOPED STATE, WHICH IS THE WHOLE REASON IT IS NOT IN
   * `appearance.ts`. The localStorage module holds what must NOT follow a person
   * between devices — a shared family laptop repainting when somebody switches
   * account is the failure `colour.md` names. These are the other kind: a
   * reader's own preferences, which should be the same on their phone.
   */
  settings: () =>
    request<{ doc: Record<string, unknown>; version: { schema: number; lamport: number }; etag: string }>(
      '/settings',
    ),

  /**
   * Write the leaves that changed, never the document.
   *
   * NO `If-Match` HERE, and that is the deliberate half of the origin's rule: a
   * single toggle is not a form. Two people flipping different switches settle
   * per leaf and neither has to retry; a client editing a whole screen of values
   * is the one that should send an etag, and it can.
   */
  putSettings: (leaves: readonly { path: string; value: unknown }[], device: string, etag?: string) =>
    request<{ etag: string; version: { schema: number; lamport: number } }>('/settings', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', ...(etag ? { 'if-match': etag } : {}) },
      body: JSON.stringify({ leaves, device }),
    }),

  /**
   * The Screener's cards, and whether it is safe to switch on.
   *
   * `ready` IS NOT ABOUT THIS LIST. It says whether the account's known-senders
   * table has anything in it at all — the thing switching the Screener on is
   * gated at the origin. A client that offered the toggle beside a full stack of
   * cards over an unseeded table would be offering to empty the Inbox.
   */
  screener: (limit?: number) =>
    request<{ senders: readonly PendingSender[]; ready: boolean; seq: number }>(
      `/screener${limit ? `?limit=${limit}` : ''}`,
    ),

  screenerDecisions: () => request<{ decisions: readonly KnownSender[] }>('/screener/decisions'),

  decideSender: (addr: string, disposition: SenderDisposition) =>
    request<KnownSender>(`/screener/${encodeURIComponent(addr)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ disposition }),
    }),

  forgetSender: (addr: string) =>
    request<{ addr: string; forgotten: boolean }>(`/screener/${encodeURIComponent(addr)}`, { method: 'DELETE' }),

  /**
   * The caller's own addresses — named aliases and disposable ones.
   *
   * NO `owner` PARAMETER, AND THAT IS THE FEATURE. The warden's read route takes
   * one, because the warden serves a household; this route does not, because the
   * origin pins the owner to `c.get('principal').account` and never reads it
   * from the request. An argument the origin ignores is an argument somebody
   * eventually expects to work, and the day it silently did nothing would be the
   * day a child listed a parent's addresses in a client that thought it had
   * asked politely.
   */
  addresses: () => request<AddressBook>('/addresses'),

  /**
   * Mint a disposable address for this account, in one round trip.
   *
   * NO PLAN AND NO DIFF, unlike every other warden verb this client reaches. The
   * two-step exists so somebody can read a change before approving it, and there
   * is nothing to read here: the origin chose the owner, the warden generated
   * the address, and the entire content of the change is the line it is about to
   * print back. A diff of it would be a confirmation dialog quoting a value the
   * reader has not seen yet.
   *
   * `site` IS A HINT, NOT A RESTRICTION. It is embedded in the local part so
   * `7fq2.petco.k3n9x2p1@` can be told from `7fq2.k3n9x2p1@` a year later; it
   * stops nobody else sending there, and a UI that called it "only for petco.com"
   * would promise a check no part of this system makes.
   *
   * AN EMPTY BOX SENDS NO KEY AT ALL. `checkSite` in the warden refuses `''`
   * with "site is required" — optional means absent, not blank — so a reader who
   * left the hint alone would otherwise get a refusal about a field they never
   * filled in. It is NOT normalised here either: `normaliseSite` lives in
   * `@hearth/core/addresses` and the warden already calls it, so a second copy
   * on this side would be one edit from disagreeing about what
   * `https://www.petco.com/cart?x=1` means — and the warden's refusal names the
   * problem better than a silent local rewrite ever could.
   */
  mintMasked: (site?: string) =>
    request<{ address: string }>('/addresses/masked', {
      method: 'POST',
      body: JSON.stringify(site !== undefined && site.trim() !== '' ? { site: site.trim() } : {}),
    }),

  /**
   * Burn one of your own disposable addresses.
   *
   * THE ORIGIN IS WHAT PROVES IT IS YOURS, and it does that by handing the
   * warden an `owner` this client never sees: the verb refuses unless the
   * address's destinations are exactly that account. The check belongs where the
   * data is — the warden holds the files — and a client that filtered its own
   * list instead would be enforcing a rule against a list it was given.
   *
   * `revoked` IS THE ADDRESS AND NOT A BOOLEAN, which is the origin's choice and
   * worth keeping in the type: a caller that only asks "did it work" reads a
   * non-empty string exactly as it reads `true`, and one that reports the
   * outcome has something to name. This client reads neither — what it shows
   * next is a fresh `GET /addresses`, because the list is the truth about what
   * exists and a locally-edited copy of it is a second one.
   */
  revokeMasked: (address: string) =>
    request<{ revoked: string }>('/addresses/masked/revoke', {
      method: 'POST',
      body: JSON.stringify({ address }),
    }),

  /**
   * Remove one of your own named aliases.
   *
   * OWNER-CHECKED AT THE WARDEN, like a revoke, and it matters more here: a
   * named alias may have several destinations, and one of them being yours is
   * not the same as it being yours to delete. `deleted` carries the address back
   * for the same reason `revoked` does.
   */
  deleteAlias: (address: string) =>
    request<{ deleted: string }>('/addresses/alias/delete', {
      method: 'POST',
      body: JSON.stringify({ address }),
    }),

  // `consoleStatus` AND `consoleSend`, NOT `console` AND `send`. `send` is
  // already this object's method for sending MAIL, and one of them would have
  // silently replaced the other — an object literal takes the last of two
  // properties with the same name, so the composer would have started posting
  // to the console.
  consoleStatus: () => request<ConsoleStatus>('/console'),

  /**
   * Arm the console, or disarm it.
   *
   * THE REASON IS NOT DECORATION. It goes in the line the origin announces
   * out of band beside the command, and it is the only part of an armed command
   * that records what somebody thought they were doing.
   */
  arm: (reason: string) =>
    request<{ armed: boolean; remainingMs: number }>('/console/arm', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),

  disarm: () =>
    request<{ armed: boolean; remainingMs: number }>('/console/arm', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ armed: false }),
    }),

  /**
   * Send one line.
   *
   * NO TAG IS SENT. The origin chooses it, and a client that supplied its own
   * could reuse one — which ends the next command's answer early and attaches
   * the rest to the one after.
   */
  consoleSend: (transport: ConsoleTransport, line: string) =>
    request<ConsoleExchange>('/console/send', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ transport, line }),
    }),

  /**
   * Ask the warden what a change would do, or tell it to do one.
   *
   * ONE CALL FOR BOTH, because they are one decision seen twice: a body with no
   * `token` is a dry run and answers with a diff, and a body with one is the
   * approval of a diff somebody has read. Splitting them would invite a client
   * that applies without ever having planned.
   */
  wardenPlan: (verb: string, args: Record<string, unknown>) =>
    request<WardenPlan>('/admin/warden/rpc', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ verb, args }),
    }),

  wardenApply: (token: string) =>
    request<{ applied: true; commits: string[]; changed: boolean }>('/admin/warden/rpc', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  /**
   * The messages this account's ingest refused to store.
   *
   * A COUNT WITH NOWHERE TO GO IS A DEAD END, and that is what this was: the
   * admin health panel has always reported `ingestSkipped`, the route behind it
   * has always existed, and NOTHING CALLED IT — so the only way to see which
   * messages were refused, and why, was `curl` over the tailnet. It is a log,
   * which is why it takes `admin.logs` and lives beside the audit trail.
   */
  ingestSkipped: () =>
    request<{ skipped: { guid: string; mailbox: string | null; uid: number | null; reason: string | null; ts: number | null }[] }>(
      '/admin/ingest-skipped',
    ),

  /**
   * The last few hundred lines the origin said.
   *
   * "WHAT DID MAILGW SAY" WAS `docker logs` OVER SSH, which is not a thing a
   * household member can run — and a household member is who notices. It is in
   * memory and gone on a restart, which is a shape rather than a limitation:
   * the container's log still has everything, and what this answers is "what has
   * just happened".
   */
  originLogs: () => request<{ lines: { at: number; text: string }[] }>('/admin/logs'),

  wardenAudit: () => request<{ entries: WardenAudit[] }>('/admin/warden/audit'),

  wardenHealth: () => request<{ ok: boolean; configLocked: boolean; ctl: boolean; pending: number }>(
    '/admin/warden/health',
  ),

  /**
   * One of the four things that have to run inside the mail container.
   *
   * NOT TWO-STEP, unlike `wardenPlan`: there is no file, so there is no diff to
   * read first. The warden answers `{verb, result}`, and `result` is whatever
   * `dms-ctl` said — "yes"/"no", "reloaded", "purged", or a tool's raw output —
   * which is why it is `unknown` here and read by the caller. A 200 means the
   * command RAN, never that it said yes.
   */
  wardenCtl: (verb: 'account.purgeMaildir' | 'dkim.keygen' | 'dovecot.reload' | 'pw.verify', args: Record<string, unknown>) =>
    request<{ verb: string; result: unknown }>('/admin/warden/ctl', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ verb, args }),
    }),

  /**
   * Prove you are still yourself, for the changes that cannot be undone.
   *
   * THE TOKEN IS NOT THIS SESSION'S. It comes from a second Access application
   * whose session expires immediately, carried here by hand from
   * `REAUTH_PATH` — `apps/mailgw/src/auth/reauth.ts` explains at length why the
   * request's own credential cannot be the second factor, and the short version
   * is that the warden is reachable only over the tailnet, where there is no
   * Access token on the request at all.
   */
  reauth: (token: string) =>
    request<{ reauthedAt: number; expiresAt: number }>('/admin/reauth', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  message: (guid: string) => request<MessageRow>(`/messages/${encodeURIComponent(guid)}`),

  body: (guid: string, options?: { links?: 'omit' }) =>
    request<MessageBody>(
      `/messages/${encodeURIComponent(guid)}/body${options?.links === 'omit' ? '?links=omit' : ''}`,
    ),

  /**
   * The HTML half, sanitised, as JSON.
   *
   * A SEPARATE REQUEST FROM `body`, on purpose: asking for HTML is a decision
   * the reading pane makes for ONE message, and folding it into the text
   * response would fetch the rich half of everything anybody hovered over.
   */
  html: (guid: string) => request<MessageHtml>(`/messages/${encodeURIComponent(guid)}/html`),

  /**
   * The permalink for a message, and the message a permalink names.
   *
   * Both authenticate exactly like every other route: a ref is an IDENTIFIER,
   * not a capability, so holding one is worth nothing without a session. The
   * origin answers an identical 404 for "no such ref" and "not yours", so a
   * caller cannot use it to learn whether somebody else's permalink is real.
   */
  ref: (guid: string) => request<{ id: string; ref: string }>(`/messages/${encodeURIComponent(guid)}/ref`),
  resolveRef: (ref: string) => request<MessageRow>(`/refs/${encodeURIComponent(ref)}`),

  /** Everything Hearth knows about one message, from rows the sync path wrote. */
  inspect: (guid: string) => request<unknown>(`/messages/${encodeURIComponent(guid)}/inspect`),

  /**
   * The URL of the original message.
   *
   * A URL AND NOT A FETCH, because the answer is a download: the origin sends
   * `Content-Disposition: attachment`, and letting the browser do it means the
   * bytes never pass through this process's memory.
   */
  rawUrl: (guid: string) => `${API}/messages/${encodeURIComponent(guid)}/raw`,

  /** The URL of one part's bytes. Not fetched here — it is an `<img>` or a link. */
  partUrl: (guid: string, part: string) =>
    `${API}/messages/${encodeURIComponent(guid)}/parts/${encodeURIComponent(part)}`,

  changes: (since: number) => request<{ changes: unknown[]; seq: number }>(`/changes?since=${since}`),

  /** Address completion, from the index. Built long before anything called it. */
  suggest: (q: string, limit = 8) =>
    request<{ suggestions: Suggestion[] }>(`/suggest?q=${encodeURIComponent(q)}&limit=${limit}`),

  /**
   * Queue a message. It does NOT send: the origin holds it for the undo window
   * and answers with the moment it becomes eligible.
   */
  send: (message: {
    /**
     * OPTIONAL, LIKE `cc` AND `bcc`, because the origin counts all three
     * together (`app.ts`: "a message needs at least one recipient"). A
     * Bcc-only message is the main reason to have a Bcc field, and typing it
     * as required here is what would make the client refuse one the server
     * would have accepted.
     */
    to?: { address: string; name?: string }[]
    cc?: { address: string; name?: string }[]
    bcc?: { address: string; name?: string }[]
    subject?: string
    text?: string
    html?: string
    inReplyTo?: string
    references?: string[]
    attachments?: number[]
  }) =>
    request<{ id: number; state: string; sendAfter: number; undoSeconds: number }>('/send', {
      method: 'POST',
      body: JSON.stringify(message),
    }),

  /**
   * Stage one file, so a message can name it at send time.
   *
   * RAW BYTES. A `File` goes straight into the body — multipart would buy a
   * boundary and a parser, and base64 would inflate a photo by a third for the
   * length of the upload.
   */
  stageAttachment: (file: File) =>
    request<{ id: number; filename: string; size: number }>(
      `/attachments?filename=${encodeURIComponent(file.name)}`,
      {
        method: 'POST',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      },
    ),

  /**
   * Everything this ACCOUNT is holding staged, which is not the same as what
   * the composer on screen has.
   *
   * THE CEILING IS PER ACCOUNT and it is 25 MB. A staged file outlives the
   * composer that uploaded it by up to a day — the origin's sweeper is the only
   * thing that has ever removed one — so a couple of abandoned drafts is an
   * account that cannot attach anything, with an error message that blames the
   * message in hand. This route has existed since attachments shipped and
   * nothing has ever called it; it is what lets a client reclaim its own leak.
   */
  stagedAttachments: () =>
    request<{ attachments: { id: number; filename: string; type: string; size: number; createdAt: number }[] }>(
      '/attachments',
    ),

  dropAttachment: (id: number) => request<{ id: number }>(`/attachments/${id}`, { method: 'DELETE' }),

  /**
   * Stage a part of a message that already exists, so a forward can carry it.
   *
   * THE BYTES GO ORIGIN → BROWSER → ORIGIN, and that is one hop more than it
   * should be. There is no route that copies a part into staging: `POST
   * /attachments` stages a request body and nothing stages a part by reference,
   * so this is what makes forwarding an attachment work with the routes that
   * exist. A `POST /attachments/from/:guid/:part` at the origin would halve the
   * traffic and is the only reason to add one.
   *
   * THE TYPE COMES FROM THE MESSAGE STRUCTURE, NOT FROM THE DOWNLOAD.
   * `GET /parts/:part` answers with a type from an allowlist — anything not on
   * it arrives as `application/octet-stream` — so re-staging what came back
   * would relabel every forwarded `.docx` as bytes. The declared type is the
   * honest one AND it was written by a stranger, so it is checked against the
   * `type/subtype` shape before it becomes a request header: `fetch` throws on a
   * header value containing a newline, and the origin writes whatever it is
   * given straight into the built message's `Content-Type`.
   */
  restagePart: async (guid: string, part: string, filename: string, type: string) => {
    // A RAW `fetch` AND NOT `request`, because the answer is bytes. The failure
    // path still goes through `failure`, so this one call site cannot end up
    // with its own idea of what an expired session looks like.
    const response = await fetch(api.partUrl(guid, part), { headers: { accept: '*/*' }, ...NO_FOLLOW })
    if (!response.ok) throw await failure(response, `That message's part ${part} could not be read`)
    const bytes = await response.blob()
    return request<{ id: number; filename: string; size: number }>(
      `/attachments?filename=${encodeURIComponent(filename)}`,
      {
        method: 'POST',
        headers: { 'content-type': MIME_TYPE.test(type) ? type : 'application/octet-stream' },
        body: bytes,
      },
    )
  },

  /**
   * What is waiting to go, and what could not go.
   *
   * THE SECOND HALF IS THE POINT. A message the submission server refuses is
   * marked `failed` and left in the table, and the only signal a sender ever
   * got was the "Message will send in Ns" toast — which a timer takes away when
   * the hold expires. A countdown reaching zero reads as "sent". Without this,
   * a permanently failed send is a row nobody can see, for ever.
   *
   * The origin strips the built message from each row (up to a megabyte), which
   * is why the tray can show the failure but not offer the text back.
   */
  outbox: () => request<{ messages: OutboxRow[] }>('/outbox'),

  /** Take a queued message back. Fails with `too_late` once it has gone. */
  unsend: (id: number) => request<{ id: number; state: string }>(`/outbox/${id}`, { method: 'DELETE' }),

  /** The rule set and whether the script on the server still matches it. */
  rules: () => request<{ ruleSet: RuleSet; drift: DriftState }>('/rules'),

  /** Compile without installing, so the UI can show what would be written. */
  checkRules: (ruleSet: RuleSet) =>
    request<{ ok?: boolean; script?: string; unsupported?: string[]; warnings?: string[] }>('/rules/check', {
      method: 'POST',
      body: JSON.stringify(ruleSet),
    }),

  saveRules: (ruleSet: RuleSet) =>
    request<{ script?: string; drift?: DriftState }>('/rules', { method: 'PUT', body: JSON.stringify(ruleSet) }),

  /**
   * Run one rule over mail that has already arrived.
   *
   * `dryRun` DEFAULTS TRUE AT THE ORIGIN, and this client is explicit either
   * way rather than relying on that — a caller reading this line should be able
   * to see which one it asked for.
   */
  applyRule: (ruleId: string, dryRun: boolean) =>
    request<{
      ruleId: string
      ruleName: string
      search: string
      matched: number
      byMailbox: { mailbox: string; matched: number }[]
      skipped: string[]
      dryRun: boolean
      applied: number
      failed?: number
    }>('/rules/apply', { method: 'POST', body: JSON.stringify({ ruleId, dryRun }) }),

  /**
   * The three POSTs behind the two ways back from a rules change.
   *
   * THEY WERE WRITTEN IN `Rules.tsx` AGAINST A HAND-ROLLED `post()`, three
   * lines that looked exactly like `request` and were missing the one thing it
   * does that `fetch` does not: an expired Access session is answered by
   * CLOUDFLARE with a 302 to a login page, `redirect: 'manual'` turns that into
   * an opaque response with status 0, and `failure()` is what recognises it,
   * mints `signed_out` and puts the sign-in prompt on screen. Without it the
   * reader who left the trial banner open over lunch pressed "Undo now" and got
   * "POST /rules/revert failed with 0" — with their filters still on trial and
   * no way offered to sign back in.
   */
  /**
   * Disarm the trial window, and say which of the three things happened.
   *
   * `kept` IS NOT ALWAYS TRUE ON A 200, and reading the status alone is how a
   * reader gets told their filters were kept when they were put back. The origin
   * answers 200 with `kept: false` twice over: `nothing_pending`, which is the
   * window having already expired and reverted, and `superseded`, which is a
   * confirm arriving from a banner about an OLDER activation — both idempotent,
   * neither an error, and neither a reason to clear the banner.
   */
  confirmRules: (changeId: string) =>
    request<{ kept: boolean; reason?: 'nothing_pending' | 'superseded' }>('/rules/confirm', {
      method: 'POST',
      body: JSON.stringify({ changeId }),
    }),

  revertRules: () =>
    request<{ reverted: boolean; activeNow?: string | null }>('/rules/revert', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /**
   * Put a retroactive run back, and the four numbers that say how it went.
   *
   * THREE OF THEM WERE BEING DROPPED. The origin has always answered `failed`,
   * `firstError` and `renumbered` beside `restored`; this type named only
   * `restored` and `notUndone`, so a reader whose mail server refused three
   * hundred of four hundred moves was told "Put back 100 messages" and nothing
   * else — a partial failure reported as a success, on the one bulk write this
   * product cannot try again.
   *
   * THEY ARE FOUR DIFFERENT FACTS WITH FOUR DIFFERENT REMEDIES.
   * `failed` is the mail server refusing now; `notUndone` is a move the server
   * never gave a landing for, which nothing can invert; `renumbered` is a
   * mailbox recreated under the journal, so the messages are somewhere, under
   * UIDs nothing here can name.
   */
  undoRuleRun: (runId: string) =>
    request<{
      restored: number
      failed?: number
      firstError?: string
      notUndone?: number
      renumbered?: number
    }>('/rules/apply/undo', { method: 'POST', body: JSON.stringify({ runId }) }),

  ops: (ops: readonly Op[]) =>
    request<{ results: OpResult[] }>('/ops', { method: 'POST', body: JSON.stringify({ ops }) }),

  /**
   * One operation applied to EVERY message a query matches.
   *
   * `expect` is the number the reader confirmed, and the origin refuses with a
   * 409 if the set has moved since — mail arrives while a dialog is open, and
   * acting on a different set from the one somebody agreed to is the failure
   * the confirmation exists to prevent.
   */
  opsOverQuery: (body: {
    q: string
    expect: number
    op: 'move' | 'flag'
    to?: string
    add?: string[]
    remove?: string[]
  }) =>
    request<{ matched: number; applied: number; failed: number; seq: number }>('/ops/query', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}
